/**
 * Section Rendering API helper.
 *
 * Liquid-tegenhanger van de route-navigatie in Hydrogen: in plaats van de hele
 * pagina te herladen halen we alleen de secties op die veranderd zijn en
 * vervangen we hun inhoud.
 *
 * Contract:
 *   Elke sectie die opnieuw gerenderd mag worden markeert zichzelf met
 *   `data-section-render="{{ section.id }}"`. Zet dat attribuut op een
 *   *binnenste* wrapper, niet op het custom element eromheen — dan blijven
 *   custom elements (<cart-drawer>, <henk-variant-switcher>) en hun
 *   gedelegeerde listeners bestaan terwijl hun inhoud vervangen wordt.
 *
 *   Gebruik nooit een hardcoded section-id: in JSON-templates heet `main` in
 *   werkelijkheid `template--<id>__main`. Lees het id uit de DOM via ids().
 *
 * Docs: https://shopify.dev/docs/api/section-rendering
 */
(() => {
  const ATTR = "data-section-render";

  /** Verzamel de section-id's die op dit moment in de DOM staan. */
  function ids(selector = `[${ATTR}]`, root = document) {
    return [
      ...new Set(
        [...root.querySelectorAll(selector)]
          .map((el) => el.getAttribute(ATTR))
          .filter(Boolean),
      ),
    ];
  }

  /**
   * Scripts uit innerHTML draaien niet vanzelf. Alleen scripts die er expliciet
   * om vragen (data-section-script) worden opnieuw ingehangen; die moeten dus
   * idempotent zijn en alleen binnen hun eigen sectie binden.
   *
   * Externe scripts die al ergens op de pagina draaien slaan we over: die
   * opnieuw uitvoeren geeft o.a. "customElements.define ... already used".
   * Ze worden alléén uitgevoerd als ze nog nergens stonden — het geval waarin
   * een sectie pas na een render voor het eerst in de DOM verschijnt.
   *
   * Bijhouden welke externe scripts al draaien kan niet per swap uit de DOM
   * afgeleid worden: de originele tags zitten vaak juist ín de sectie die we
   * vervangen en zijn dan al weg. Vandaar een set die de hele paginasessie
   * meegaat, geseed met de initiële DOM (dit script is `defer`).
   */
  const executedSrc = new Set(
    [...document.querySelectorAll("script[src]")].map((el) => el.src),
  );

  function reviveScripts(root) {
    const scripts = [...root.querySelectorAll("script[data-section-script]")];
    if (!scripts.length) return;

    for (const old of scripts) {
      if (old.src) {
        if (executedSrc.has(old.src)) continue;
        executedSrc.add(old.src);
      }

      const script = document.createElement("script");
      for (const { name, value } of [...old.attributes]) {
        script.setAttribute(name, value);
      }
      script.textContent = old.textContent;
      old.replaceWith(script);
    }
  }

  /**
   * Open/dicht-stand van <details data-preserve-open="key"> meenemen over de
   * swap heen.
   *
   * Dit moet in de *bron* gebeuren, vóór de innerHTML-assignment, niet erna op
   * het resultaat. Een <details> die dicht de DOM in komt en daarna `open`
   * krijgt, animeert: de transitie op `details::details-content` heeft dan een
   * beginwaarde (block-size 0) om vanaf te lopen. Komt hij al mét [open] binnen,
   * dan is `block-size: auto` de eerste berekende waarde en speelt er niets af —
   * precies wat je wil als de gebruiker de dropdown al open had staan.
   *
   * De server-default in de opgehaalde HTML wordt bewust overschreven, in beide
   * richtingen: een dichtgeklapte <details open> hoort ook dicht te blijven.
   */
  function preserveOpen(target, source) {
    for (const el of target.querySelectorAll("[data-preserve-open]")) {
      const key = el.dataset.preserveOpen;
      if (!key) continue;
      const fresh = source.querySelector(
        `[data-preserve-open="${CSS.escape(key)}"]`,
      );
      if (fresh) fresh.toggleAttribute("open", Boolean(el.open));
    }
  }

  /**
   * Vervang de inhoud van het DOM-element met dit section-id door de inhoud van
   * `source` (het gelijknamige element uit de opgehaalde HTML). De wrapper zelf
   * blijft staan, alleen de innerHTML gaat eruit.
   */
  function swap(id, source) {
    const target = document.querySelector(`[${ATTR}="${CSS.escape(id)}"]`);
    if (!target || !source) return false;

    // Scrollpositie van scrollbare kinderen bewaren over de swap heen.
    const scroll = [...target.querySelectorAll("[data-preserve-scroll]")].map(
      (el) => [el.dataset.preserveScroll, el.scrollTop],
    );

    preserveOpen(target, source);

    target.innerHTML = source.innerHTML;

    // Data-attributen (bv. data-item-count) meenemen; die staan op de wrapper
    // zelf en zouden anders achterblijven op de oude waarde.
    for (const { name, value } of [...source.attributes]) {
      if (name.startsWith("data-") && name !== ATTR) {
        target.setAttribute(name, value);
      }
    }

    for (const [key, top] of scroll) {
      const el = target.querySelector(`[data-preserve-scroll="${key}"]`);
      if (el) el.scrollTop = top;
    }

    reviveScripts(target);

    target.dispatchEvent(
      new CustomEvent("section:rendered", {
        bubbles: true,
        detail: { sectionId: id },
      }),
    );

    return true;
  }

  /** Vervang één sectie op basis van de opgeleverde HTML-string. */
  function applyOne(id, html) {
    if (typeof html !== "string") return false;
    const parsed = new DOMParser().parseFromString(html, "text/html");
    const source =
      parsed.querySelector(`[${ATTR}="${CSS.escape(id)}"]`) ||
      parsed.querySelector(`[${ATTR}]`);
    return swap(id, source);
  }

  /**
   * Vervang meerdere secties in één keer. `sections` is { id: html }.
   *
   * De sleutel uit de response wordt bewust niet vertrouwd: afhankelijk van de
   * request kan Shopify op section-id óf op section-type keyen. We lezen het
   * id daarom uit de `data-section-render` in de opgeleverde HTML zelf.
   */
  function apply(sections) {
    if (!sections) return [];
    const applied = [];

    for (const [key, html] of Object.entries(sections)) {
      if (typeof html !== "string") continue;
      const parsed = new DOMParser().parseFromString(html, "text/html");
      const sources = [...parsed.querySelectorAll(`[${ATTR}]`)];

      if (!sources.length) continue;

      for (const source of sources) {
        const id = source.getAttribute(ATTR) || key;
        if (swap(id, source)) applied.push(id);
      }
    }

    return applied;
  }

  /**
   * Haal secties op voor een willekeurige storefront-URL en render ze.
   * Gebruikt door de PDP variant switcher: dezelfde URL + ?variant=<id>.
   *
   * Let op: géén `Accept: application/json` meesturen. Met die header keyt
   * Shopify de response op section-*type* in plaats van op het gevraagde
   * section-id, en levert hij andere HTML op.
   */
  async function render(sectionIds, url = window.location.href) {
    const list = (Array.isArray(sectionIds) ? sectionIds : [sectionIds]).filter(
      Boolean,
    );
    if (!list.length) return {};

    const target = new URL(url, window.location.origin);
    target.searchParams.set("sections", list.join(","));

    const res = await fetch(target.toString());
    if (!res.ok) throw new Error(`section rendering ${res.status}`);

    const sections = await res.json();
    apply(sections);
    return sections;
  }

  window.HenkSections = { ATTR, ids, apply, applyOne, swap, render };
})();
